import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import * as UUID from 'uuid';
import { ReactComponent as BackArrow } from '../../assets/icons/back-arrow.svg';
import GalleryTopBar from '../../components/GalleryTopBar';
import TemplatePicker from '../../components/TemplatePicker';
import { useCreateDocument } from '../../hooks/document/document';
import { useApplyDocumentOperation } from '../../hooks/document/apply';
import { useDocumentStore } from '../../hooks/store/document';
import { AddBlockOperation, AddPageOperation } from '../../hooks/store/document/operations';
import { getBlockType } from '../../configuration/blocks';
import { DOCUMENT_STARTERS } from '../../configuration/starters';
import { DocumentFormat } from '../../types/DocumentSettings';
import * as NewspapersAPI from '../../api/Newspapers';
import * as StoriesAPI from '../../api/Stories';
import * as DocumentsAPI from '../../api/Documents';
import { Edition, Story, StoryTemplate, STORY_TEMPLATE_LABELS } from '../../types/models/Newspaper';
import { track, Events } from '../../utils/analytics';

const TEMPLATE_TO_WRITING_PLAN: Record<string, string> = {
  news_story: 'news-article',
  feature_story: 'news-article',
  profile_interview: 'news-article',
  book_review: 'book-review',
  movie_review: 'book-review',
  video_game_review: 'book-review',
  restaurant_review: 'restaurant-review',
  recipe: 'restaurant-review',
  opinion: 'op-ed',
  photo_essay: 'news-article',
};

const NEWSPAPER_PROMPTS: Record<string, string> = {
  news_story: 'Write a news article about something that happened at your school or in your community. A good news story answers: Who was involved? What happened? When and where did it happen? Why does it matter?',
  feature_story: 'Write a feature story about something interesting at your school. A feature story goes deeper than a news article \u2014 tell us the story behind the story. What makes this topic special?',
  profile_interview: 'Interview someone interesting \u2014 a teacher, a student, a community member \u2014 and write about them. What do they do? What makes them unique? Include at least one quote from your interview.',
  book_review: 'Write a review of a book you read recently. Tell your readers what the book is about (without spoiling the ending!), what you liked or didn\'t like, and whether you\'d recommend it.',
  movie_review: 'Write a review of a movie you watched. What was the movie about? What did you think of the acting, the story, and the visuals? Would you recommend it to your classmates?',
  video_game_review: 'Write a review of a video game you\'ve played. What\'s the game about? What makes it fun (or not fun)? How would you rate it? Who would you recommend it to?',
  restaurant_review: 'Write a review of a restaurant or cafeteria meal. What did you order? How did it taste? How was the experience? Would you go back or recommend it to a friend?',
  recipe: 'Share a recipe for one of your favorite foods. Write the ingredients, the step-by-step instructions, and a short paragraph about why you love this dish.',
  opinion: 'Write an opinion piece about something you feel strongly about. State your opinion clearly, give at least two reasons to support it, and explain why your readers should care.',
  photo_essay: 'Tell a story using photos and words. Pick a topic, take or find photos that show different parts of the story, and write captions that explain what\'s happening in each image.',
};

export default function StudentEditionView() {
  const { id: newspaperId, editionId } = useParams<{ id: string; editionId: string }>();
  const navigate = useNavigate();

  const [edition, setEdition] = useState<Edition | null>(null);
  const [newspaperName, setNewspaperName] = useState('');
  const [myStory, setMyStory] = useState<Story | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Use the same document creation hooks as "New Pressto"
  const createDocument = useCreateDocument(false); // false = don't auto-initialize, we'll add blocks manually
  const apply = useApplyDocumentOperation();

  useEffect(() => {
    if (!newspaperId || !editionId) return;

    const load = async () => {
      setIsLoading(true);
      try {
        const npRes = await NewspapersAPI.getById(newspaperId);
        setNewspaperName(npRes.data.data.name);

        const edRes = await NewspapersAPI.getEditions(newspaperId);
        const found = edRes.data.data.find((e: Edition) => e.id === editionId);
        if (found) setEdition(found);

        const storiesRes = await StoriesAPI.getAll(newspaperId, editionId);
        const stories: Story[] = Array.isArray(storiesRes.data) ? storiesRes.data : (storiesRes.data as any).data;
        if (stories.length > 0) {
          setMyStory(stories[0]);
        }
      } catch {
        // silent
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [newspaperId, editionId]);

  const handleSelectTemplate = async (templateType: StoryTemplate) => {
    if (!newspaperId || !editionId || isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      // If switching, delete old story and its document
      if (myStory) {
        if (myStory.document_id) {
          try { await DocumentsAPI.deleteDocument(myStory.document_id); } catch {}
        }
        try { await StoriesAPI.deleteStory(newspaperId, editionId, myStory.id); } catch {}
        setMyStory(null);
      }

      track(Events.PickTemplate, {
        template_type: templateType,
        newspaper_id: newspaperId,
        edition_id: editionId,
      });

      // 1. Create the story record (no document yet)
      const storyRes = await StoriesAPI.create(newspaperId, editionId, {
        template_type: templateType,
        title: STORY_TEMPLATE_LABELS[templateType],
      });
      const story = storyRes.data.data ?? storyRes.data;

      // 2. Create the document using the same flow as "New Pressto"
      const writingPlanId = TEMPLATE_TO_WRITING_PLAN[templateType] || 'op-ed';
      const prompt = NEWSPAPER_PROMPTS[templateType];

      const document = await createDocument(
        STORY_TEMPLATE_LABELS[templateType],
        DocumentFormat.DOC,
        {
          writingPlan: writingPlanId,
          newspaperPrompt: prompt,
        },
      );

      // 3. Add blocks from the matching starter
      const starter = DOCUMENT_STARTERS.find((s) => s.writingPlanId === writingPlanId);
      if (starter) {
        for (const page of starter.pages) {
          const pageOp = new AddPageOperation(document.id);
          apply(pageOp);
          for (let i = 0; i < page.blocks.length; i += 1) {
            const block = page.blocks[i];
            apply(new AddBlockOperation(
              document.id,
              pageOp.pageId,
              UUID.v1(),
              getBlockType(block.typeId),
              { x: 1, y: i + 1, width: 1, height: 1 },
            ));
          }
        }
      }

      // 4. Link document to story
      await StoriesAPI.update(newspaperId, editionId, story.id, {
        document_id: document.id,
      });

      // 5. Attach story info to document in store so editor shows Turn in button
      const storeDoc = useDocumentStore.getState().documentsById[document.id];
      if (storeDoc) {
        useDocumentStore.getState().setDocument({
          ...storeDoc,
          story: {
            id: story.id,
            edition_id: editionId,
            newspaper_id: newspaperId,
            newspaper_name: '',
            edition_name: edition?.name ?? '',
            template_type: templateType,
            title: STORY_TEMPLATE_LABELS[templateType],
            status: 'draft',
          },
        });
      }

      // 6. Navigate to the document editor
      navigate(`/document/${document.id}/`);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Something went wrong. Try again.');
    } finally {
      setIsCreating(false);
    }
  };

  const handleOpenDocument = () => {
    if (myStory?.document_id) {
      navigate(`/document/${myStory.document_id}/`);
    }
  };

  window.document.title = edition?.name ?? 'Edition';

  if (isLoading) {
    return (
      <>
        <GalleryTopBar tab="documents" />
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <GalleryTopBar tab="documents" />
      <div style={{ background: '#F0F0F4', minHeight: 'calc(100vh - 64px)', padding: '2rem' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate('/gallery/documents')} size="small">
            <BackArrow height={20} width={20} stroke="#163567" />
          </IconButton>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {newspaperName}
            </Typography>
            <Typography variant="h5" fontWeight={700} color="#163567">
              {edition?.name ?? '\u2026'}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {edition && edition.status !== 'open' ? (
          <Box
            sx={{
              background: '#fff',
              borderRadius: 2,
              p: 3,
              maxWidth: 500,
              border: '2px solid #E0E0E0',
            }}
          >
            <Typography variant="h6" fontWeight={700} color="#163567" sx={{ mb: 1 }}>
              This edition isn&apos;t open yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {edition.status === 'published'
                ? 'This edition has already been published. Check the newsstand to read it.'
                : 'Your teacher will share it when it\u2019s ready. Check back soon!'}
            </Typography>
          </Box>
        ) : myStory && !showPicker && myStory.document_id ? (
          <>
            <Box
              onClick={handleOpenDocument}
              sx={{
                background: '#fff',
                borderRadius: 2,
                p: 3,
                maxWidth: 400,
                cursor: 'pointer',
                border: '2px solid #163567',
                '&:hover': { boxShadow: '0 4px 12px rgba(22,53,103,0.2)' },
              }}
            >
              <Typography variant="overline" color="text.secondary">
                Your story
              </Typography>
              <Typography variant="h6" fontWeight={700} color="#163567">
                {myStory.title ?? STORY_TEMPLATE_LABELS[myStory.template_type as StoryTemplate] ?? myStory.template_type}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {STORY_TEMPLATE_LABELS[myStory.template_type as StoryTemplate]}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="#163567" sx={{ mt: 2 }}>
                Continue writing &rarr;
              </Typography>
            </Box>
            <Typography
              variant="body2"
              onClick={() => setShowPicker(true)}
              sx={{ mt: 2, cursor: 'pointer', color: '#888', textDecoration: 'underline', '&:hover': { color: '#163567' } }}
            >
              Choose a different type of story
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h6" fontWeight={600} color="#163567" sx={{ mb: 0.5 }}>
              What kind of story do you want to write?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Pick a type of article for this edition. You can get started right away!
            </Typography>
            {myStory && (
              <Typography
                variant="body2"
                onClick={() => setShowPicker(false)}
                sx={{ mb: 2, cursor: 'pointer', color: '#888', textDecoration: 'underline', '&:hover': { color: '#163567' } }}
              >
                &larr; Never mind, keep my {STORY_TEMPLATE_LABELS[myStory.template_type as StoryTemplate]}
              </Typography>
            )}
            <TemplatePicker onSelect={handleSelectTemplate} disabled={isCreating} />
            {isCreating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Setting up your story...</Typography>
              </Box>
            )}
          </>
        )}
      </div>
    </>
  );
}
